import { FC, useState, useContext, useEffect } from "react";
import { useIntl } from "react-intl";
import { useFormik, getIn } from "formik";
import * as Yup from "yup";
import clsx from "clsx";
import ErrorMessage1 from "../../components/ErrorMessage1";
import Alert1 from "../../components/Alert1";
import ConstantsContext from "../../../store/Constants-context";
import CustomerDataContext from "../../../store/CustomerData-context";
import { PageTitle } from "../../../_metronic/layout/core";
import Select from 'react-select';
import {saveExternalProvider, getExternalProvider, deleteExternalProvider} from "./userManagement-hooks";
import Cookies from "universal-cookie";

const cookies = new Cookies();

// Create dynamic validation schema based on selected provider
const createValidationSchema = (selectedProvider: string | null) => {
    return Yup.object().shape({
        clientId: Yup.string()
            .trim("Cannot include leading and trailing spaces")
            .min(1, "Minimum 1 character required.")
            .max(255, "Maximum 255 characters allowed.")
            .required("Client ID is required"),
        clientSecret: Yup.string()
            .trim("Cannot include leading and trailing spaces")
            .min(1, "Minimum 1 character required.")
            .max(255, "Maximum 255 characters allowed.")
            .required("Client Secret is required"),
        tenantId: Yup.string()
            .trim("Cannot include leading and trailing spaces")
            .min(1, "Minimum 1 character required.")
            .max(255, "Maximum 255 characters allowed.")
            .when([], {
                is: () => selectedProvider === 'microsoft',
                then: (schema) => schema.required("Tenant ID is required"),
                otherwise: (schema) => schema.notRequired()
            }),
        oktaDomain: Yup.string()
            .trim("Cannot include leading and trailing spaces")
            .test('valid-domain', 'Please enter a valid domain name', function(value) {
                // Handle undefined/null values
                if (!value) {
                    return selectedProvider !== 'okta'; // true for non-Okta (optional), false for Okta (required)
                }

                // Validate domain format for non-empty values
                const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
                return domainRegex.test(value);
            })
            .max(255, "Maximum 255 characters allowed.")
            .when([], {
                is: () => selectedProvider === 'okta',
                then: (schema) => schema.required("Okta Domain is required"),
                otherwise: (schema) => schema.notRequired()
            }),
        identifiers: Yup.string()
            .trim("Cannot include leading and trailing spaces")
            .test('valid-domains', 'Please enter valid domain names separated by commas', function(value) {
                if (!value) return true; // Allow empty since it's not required

                const domains = value.split(',').map(domain => domain.trim());
                const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

                return domains.every(domain => {
                    if (domain === '') return false; // No empty domains after splitting
                    return domainRegex.test(domain);
                });
            })
            .max(1000, "Maximum 1000 characters allowed.")
            .required("Identifier is required"),
    });
};

const ExternalProvider = () => {
    const [providerData, setProviderData] = useState({
        external_provider: "",
        client_id: "",
        client_secret: "",
        oidc_issuer: "",
        identifiers: "",
    });
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [deleteSuccess, setDeleteSuccess] = useState(false);
    const [error, setError] = useState(false);
    const [deleteError, setDeleteError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [deleteErrorMessage, setDeleteErrorMessage] = useState("");
    const [selectedOption, setSelectedOption] = useState<{value: string, label: string} | null>(null);
    const [hasExistingData, setHasExistingData] = useState(false);

    const intl = useIntl();
    const customerCtx = useContext(CustomerDataContext);
    const constantsCtx = useContext(ConstantsContext);

    const providerOptions = [
        { value: 'microsoft', label: 'Microsoft' },
        { value: 'okta', label: 'Okta' }
    ];

    // This would be where you'd fetch existing configuration if needed
    useEffect(() => {
        // Future implementation: fetch existing external provider configuration
        const fetchProviderData = async () => {
            try {
                setLoading(true);
                const data = await getExternalProvider();
                setProviderData(data.returnData);
                const selected:any = providerOptions.find(opt => opt.value === data.returnData.external_provider);
                setSelectedOption(selected);
                setHasExistingData(!!data.returnData.external_provider);
            } catch (err) {
                setError(true);
                console.error('Error fetching provider data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchProviderData();
    }, []);

    const saveExternalProviderConfig = async (values:any) => {
        setLoading(true);
        setSuccess(false);
        setError(false);

        values.externalProvider = selectedOption?.value;
        try {
            const response = await saveExternalProvider(values);

            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));

            setSuccess(true);
            setHasExistingData(true);

            let customerData = customerCtx.customer_data as any;
            customerData.external_provider = values.externalProvider;
            customerCtx.setCustomerDataCtx(customerData);
            cookies.set('external_provider', values.externalProvider);

            setLoading(false);
        } catch (err:any) {
            setError(true);
            setErrorMessage(err.message || "Failed to save SSO provider configuration");
            setLoading(false);
        }
    };

    const handleDeleteExternalProvider = async () => {
        if (!window.confirm("Are you sure you want to delete the SSO provider configuration? This action cannot be undone.")) {
            return;
        }

        setDeleting(true);
        setDeleteSuccess(false);
        setDeleteError(false);
        setDeleteErrorMessage("");

        try {
            await deleteExternalProvider();

            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));

            setDeleteSuccess(true);
            setHasExistingData(false);

            let customerData = customerCtx.customer_data as any;
            customerData.external_provider = '';
            customerCtx.setCustomerDataCtx(customerData);
            cookies.set('external_provider', '');

            // Reset form and state
            formik.resetForm();
            setSelectedOption(null);
            setProviderData({
                external_provider: "",
                client_id: "",
                client_secret: "",
                oidc_issuer: "",
                identifiers: "",
            });

            // Clear delete success message after 3 seconds
            setTimeout(() => {
                setDeleteSuccess(false);
            }, 3000);

        } catch (err:any) {
            setDeleteError(true);
            setDeleteErrorMessage(err.message || "Failed to delete SSO provider configuration");
        } finally {
            setDeleting(false);
        }
    };

    const formik = useFormik({
        initialValues: {
            externalProvider: providerData.external_provider || "",
            clientId: providerData.client_id || "",
            clientSecret: providerData.client_secret || "",
            tenantId: providerData.external_provider === "microsoft" ? (providerData.oidc_issuer ? providerData.oidc_issuer.trim().split('/')[3] || "" : "") : "",
            oktaDomain: providerData.external_provider === "okta" ? (providerData.oidc_issuer ? providerData.oidc_issuer.trim().split('/')[2] || "" : "") : "",
            identifiers: providerData.identifiers ? providerData.identifiers.toString() || "" : "",
        },
        validationSchema: createValidationSchema(selectedOption?.value || null),
        enableReinitialize: true,
        onSubmit: async (values) => {
            await saveExternalProviderConfig(values);

            // Reset form state after success
            setTimeout(() => {
                setSuccess(false);
                formik.setTouched({
                    externalProvider: false,
                    clientId: false,
                    clientSecret: false,
                    tenantId: false,
                    oktaDomain: false,
                    identifiers: false
                });  // Reset touched state for all fields
            }, 3000);
        },
        validateOnMount: true, // This will validate the form on initial render
    });

    // Check if all required fields are filled based on selected provider
    const isFormFilled = Boolean(
        formik.values.clientId &&
        formik.values.clientSecret &&
        (selectedOption?.value === 'microsoft' ? formik.values.tenantId : true) &&
        (selectedOption?.value === 'okta' ? formik.values.oktaDomain : true)
    );

    // Handle provider change
    const handleProviderChange = (option: any) => {
        setSelectedOption(option);
        // Clear the fields that are not relevant to the selected provider
        if (option?.value === 'microsoft') {
            formik.setFieldValue('oktaDomain', '');
            formik.setFieldTouched('oktaDomain', false);
        } else if (option?.value === 'okta') {
            formik.setFieldValue('tenantId', '');
            formik.setFieldTouched('tenantId', false);
        } else {
            // Clear both fields if no provider selected
            formik.setFieldValue('tenantId', '');
            formik.setFieldValue('oktaDomain', '');
            formik.setFieldTouched('tenantId', false);
            formik.setFieldTouched('oktaDomain', false);
        }
    };

    return (
        <>
            {/* Setting Active Page Name in the Context */}
            <PageTitle activePageName={"external_provider"}></PageTitle>

            {/* begin::Row */}
            <div className="row gy-5 gx-xl-8">
                <div className="col-xxl-12">
                    <div className="card card-custom">
                        <div className="card-header">
                            <div className="card-title">
                                SSO Provider Configuration using OpenID Connect (OIDC)
                            </div>
                        </div>
                        <div className="card-body">
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    formik.handleSubmit();
                                }}
                            >
                                <div className="row mb-10">
                                    <div className="col-lg-6 col-xl-6" id="externalProvider">
                                        <label className="col-lg-12 col-form-label">
                                            SSO Provider
                                        </label>
                                        <Select
                                            options={providerOptions}
                                            value={selectedOption}
                                            onChange={handleProviderChange}
                                            placeholder="Select an SSO provider..."
                                        />
                                    </div>
                                </div>
                                <div className="row mb-10">
                                    <div className="col-lg-6 col-xl-6">
                                        <label className="col-lg-12 col-form-label">
                                            Client ID <small style={{color: "red"}}>*</small>
                                            <small className="text-muted d-block">
                                                Enter the client id from your registered app
                                            </small>
                                        </label>
                                        <input
                                            type="text"
                                            className={clsx(
                                                "form-control form-control-solid px-5",
                                                {
                                                    "is-invalid":
                                                        formik.touched.clientId && formik.errors.clientId,
                                                },
                                                {
                                                    "is-valid":
                                                        formik.touched.clientId && !formik.errors.clientId,
                                                }
                                            )}
                                            {...formik.getFieldProps("clientId")}
                                        />
                                        {formik.touched.clientId && formik.errors.clientId && (
                                            <ErrorMessage1
                                                color={constantsCtx.ERROR_MESSAGE_COLOR_RED}
                                                errorMessage={getIn(formik.errors, 'clientId') || ''}
                                            />
                                        )}
                                    </div>
                                </div>

                                <div className="row mb-10">
                                    <div className="col-lg-6 col-xl-6">
                                        <label className="col-lg-12 col-form-label">
                                            Client Secret <small style={{color: "red"}}>*</small>
                                            <small className="text-muted d-block">
                                                Enter the client secret from your registered app
                                            </small>
                                        </label>
                                        <input
                                            type="password"
                                            className={clsx(
                                                "form-control form-control-solid px-5",
                                                {
                                                    "is-invalid":
                                                        formik.touched.clientSecret && formik.errors.clientSecret,
                                                },
                                                {
                                                    "is-valid":
                                                        formik.touched.clientSecret && !formik.errors.clientSecret,
                                                }
                                            )}
                                            {...formik.getFieldProps("clientSecret")}
                                        />
                                        {formik.touched.clientSecret && formik.errors.clientSecret && (
                                            <ErrorMessage1
                                                color={constantsCtx.ERROR_MESSAGE_COLOR_RED}
                                                errorMessage={getIn(formik.errors, 'clientSecret') || ''}
                                            />
                                        )}
                                    </div>
                                </div>

                                {/* Conditional Tenant ID field for Microsoft */}
                                {selectedOption?.value === 'microsoft' && (
                                    <div className="row mb-10">
                                        <div className="col-lg-6 col-xl-6">
                                            <label className="col-lg-12 col-form-label">
                                                Tenant ID <small style={{color: "red"}}>*</small>
                                                <small className="text-muted d-block">
                                                    Enter the Directory (tenant) ID from your registered app
                                                </small>
                                            </label>
                                            <input
                                                type="text"
                                                className={clsx(
                                                    "form-control form-control-solid px-5",
                                                    {
                                                        "is-invalid":
                                                            formik.touched.tenantId && formik.errors.tenantId,
                                                    },
                                                    {
                                                        "is-valid":
                                                            formik.touched.tenantId && !formik.errors.tenantId,
                                                    }
                                                )}
                                                {...formik.getFieldProps("tenantId")}
                                            />
                                            {formik.touched.tenantId && formik.errors.tenantId && (
                                                <ErrorMessage1
                                                    color={constantsCtx.ERROR_MESSAGE_COLOR_RED}
                                                    errorMessage={getIn(formik.errors, 'tenantId') || ''}
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Conditional Okta Domain field for Okta */}
                                {selectedOption?.value === 'okta' && (
                                    <div className="row mb-10">
                                        <div className="col-lg-6 col-xl-6">
                                            <label className="col-lg-12 col-form-label">
                                                Okta Domain <small style={{color: "red"}}>*</small>
                                                <small className="text-muted d-block">
                                                    Enter your Okta domain (e.g., company.okta.com)
                                                </small>
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="company.okta.com"
                                                className={clsx(
                                                    "form-control form-control-solid px-5",
                                                    {
                                                        "is-invalid":
                                                            formik.touched.oktaDomain && formik.errors.oktaDomain,
                                                    },
                                                    {
                                                        "is-valid":
                                                            formik.touched.oktaDomain && !formik.errors.oktaDomain,
                                                    }
                                                )}
                                                {...formik.getFieldProps("oktaDomain")}
                                            />
                                            {formik.touched.oktaDomain && formik.errors.oktaDomain && (
                                                <ErrorMessage1
                                                    color={constantsCtx.ERROR_MESSAGE_COLOR_RED}
                                                    errorMessage={getIn(formik.errors, 'oktaDomain') || ''}
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="row mb-10">
                                    <div className="col-lg-6 col-xl-6">
                                        <label className="col-lg-12 col-form-label">
                                            Identifiers <small style={{color: "red"}}>*</small>
                                            <small className="text-muted d-block">
                                                Enter your corporate email domain names separated by commas (e.g., loopsafe.net, anz.com)
                                            </small>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="google.com, hotmail.com"
                                            className={clsx(
                                                "form-control form-control-solid px-5",
                                                {
                                                    "is-invalid":
                                                        formik.touched.identifiers && formik.errors.identifiers,
                                                },
                                                {
                                                    "is-valid":
                                                        formik.touched.identifiers && !formik.errors.identifiers && formik.values.identifiers,
                                                }
                                            )}
                                            {...formik.getFieldProps("identifiers")}
                                        />
                                        {formik.touched.identifiers && formik.errors.identifiers && (
                                            <ErrorMessage1
                                                color={constantsCtx.ERROR_MESSAGE_COLOR_RED}
                                                errorMessage={getIn(formik.errors, 'identifiers') || ''}
                                            />
                                        )}
                                    </div>
                                </div>

                                <div className="row mb-10">
                                    {/* begin::Success Alert */}
                                    {!loading && success && (
                                        <Alert1
                                            alertTitle="Success"
                                            alertMessage="SSO Provider Configuration Saved"
                                            success={true}
                                        />
                                    )}
                                    {/* end::Success Alert */}

                                    {/* begin::Delete Success Alert */}
                                    {!deleting && deleteSuccess && (
                                        <Alert1
                                            alertTitle="Success"
                                            alertMessage="SSO Provider Configuration Deleted"
                                            success={true}
                                        />
                                    )}
                                    {/* end::Delete Success Alert */}

                                    {/* begin::Error Alert */}
                                    {!loading && error && (
                                        <Alert1
                                            alertTitle={"Error"}
                                            alertMessage={errorMessage || "Something went wrong"}
                                            success={false}
                                        />
                                    )}
                                    {/* end::Error Alert */}

                                    {/* begin::Delete Error Alert */}
                                    {!deleting && deleteError && (
                                        <Alert1
                                            alertTitle={"Error"}
                                            alertMessage={deleteErrorMessage || "Failed to delete configuration"}
                                            success={false}
                                        />
                                    )}
                                    {/* end::Delete Error Alert */}
                                </div>

                                <div className="row mb-10">
                                    <div className="col-lg-6 col-xl-6">
                                        <button
                                            type="button"
                                            className="btn btn-active-light btn-color-muted cancelButton"
                                            onClick={() => formik.resetForm()}
                                        >
                                            Reset
                                        </button>
                                    </div>
                                    <div className="col-lg-6 col-xl-6 d-flex gap-3">
                                        {hasExistingData && (
                                            <button
                                                type="button"
                                                className="btn btn-danger"
                                                onClick={handleDeleteExternalProvider}
                                                disabled={deleting || loading}
                                            >
                                                {deleting && (
                                                    <span className="indicator-progress">
                                                        Deleting...{" "}
                                                    </span>
                                                )}
                                                {!deleting && (
                                                    <span className="indicator-label">Delete</span>
                                                )}
                                            </button>
                                        )}
                                        <button
                                            type="submit"
                                            className="btn btn-ll"
                                            disabled={
                                                formik.isSubmitting || !formik.isValid || success || !isFormFilled
                                            }
                                        >
                                            {success && !loading && (
                                                <span className="indicator-label">Saved</span>
                                            )}
                                            {!success && loading && !error && (
                                                <span className="indicator-progress">
                                                Please wait...{" "}
                                            </span>
                                            )}
                                            {!success && !loading && (
                                                <span className="indicator-label">Save</span>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export {ExternalProvider};